// Carrier-label output helpers shared by the pack desk (Confirm Shipment)
// and the Orders page reprint action, so a reprint takes exactly the route
// the original label took:
//
//   ZPL → network print agent (saved Zebra on any warehouse PC)
//       → local print agent on this machine (/print-zpl, agent ≥ 1.6.0)
//   PDF → hidden iframe + window.print() (non-Zebra desks / no agent)
//
// Nothing here fetches: callers hand in the base64 content they got from
// the BE (booking row ZPL, carrier PDF, or an own-label attachment).
import { silentPrintZplRaw, getSavedLocalPrinter } from "./printAgent";
import { submitNetworkPrintJob, getSavedNetworkPrinter } from "./networkPrint";

export type PrintMode = "local" | "network" | null;
export type PrintResult = { success: boolean; error?: string };

/** Decode base64 into raw bytes (no data: prefix expected). */
export const base64ToBytes = (b64: string): Uint8Array => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
};

/** Decode a base64 ZPL document to the string the print agent expects.
 *  `atob` alone yields one char per BYTE (Latin-1), so a UTF-8 label (DPD:
 *  "Państwa dane…", "Wrocław", with ^CI28) was re-encoded on the way to the
 *  agent and printed as "PaÅstwa" / "WrocÅaw" (2026-10-05). APC/InPost ZPL
 *  is ASCII, where the two decodings coincide. */
export const base64ToZpl = (b64: string): string => {
    const bytes = base64ToBytes(b64);
    try {
        if (typeof TextDecoder !== "undefined") return new TextDecoder("utf-8").decode(bytes);
        // jsdom (tests) and very old WebViews: percent-decode the UTF-8 bytes.
        let pct = "";
        for (let i = 0; i < bytes.length; i++) pct += `%${bytes[i].toString(16).padStart(2, "0")}`;
        return decodeURIComponent(pct);
    } catch {
        // Not valid UTF-8 after all — hand over the bytes as Latin-1, which
        // is what the printer always received before.
        return atob(b64);
    }
};

/** Blob → raw base64 (data: prefix stripped) — for attachments that have
 *  to go through printPdfBase64. */
export const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const r = String(reader.result ?? "");
            const comma = r.indexOf(",");
            resolve(comma >= 0 ? r.slice(comma + 1) : r);
        };
        reader.onerror = () => reject(new Error("read_failed"));
        reader.readAsDataURL(blob);
    });

/** Print a base64 PDF via a hidden iframe (the PDF-fallback path for
 *  carrier labels on non-Zebra desks / outdated print agents). Resolves
 *  true once the print dialog has been handed the document. */
export const printPdfBase64 = (b64: string): Promise<boolean> =>
    new Promise((resolve) => {
        try {
            const url = URL.createObjectURL(new Blob([base64ToBytes(b64)], { type: "application/pdf" }));
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = url;
            iframe.onload = () => {
                try {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                    resolve(true);
                } catch { resolve(false); }
                // focus() put the keyboard inside the hidden PDF frame and it
                // stayed there once the dialog closed, so the scanner typed
                // into the PDF viewer instead of the page's keydown listener:
                // the tote scan after every order looked frozen until the
                // packer left Dispatch (Pip Guest, NPDESK12 — PDF route on
                // every label, 2026-10-26). Hand focus back to the page for as
                // long as the frame lives; the dialog can close after print()
                // has returned.
                const reclaimFocus = () => {
                    if (document.activeElement !== iframe) return;
                    iframe.blur();
                    window.focus();
                };
                reclaimFocus();
                const focusGuard = setInterval(reclaimFocus, 500);
                // Leave time for the print dialog to grab the document.
                setTimeout(() => { clearInterval(focusGuard); URL.revokeObjectURL(url); iframe.remove(); }, 60000);
            };
            document.body.appendChild(iframe);
        } catch { resolve(false); }
    });

/** Merge several base64 PDFs into one base64 PDF, pages in list order. */
export const mergePdfsBase64 = async (list: string[]): Promise<string> => {
    const { PDFDocument } = await import("pdf-lib");
    const out = await PDFDocument.create();
    for (const b64 of list) {
        const src = await PDFDocument.load(base64ToBytes(b64));
        const pages = await out.copyPages(src, src.getPageIndices());
        pages.forEach((p) => out.addPage(p));
    }
    return out.saveAsBase64();
};

/** Print several label PDFs through ONE print dialog. printPdfBase64
 *  resolves as soon as print() is called, so printing PDFs one after
 *  another fired the second print() while the first dialog was still
 *  open and the browser dropped it: a 2-box APC order on a desk with no
 *  Zebra route printed box 1 only, every time (JUK727980, 2026-10-26 —
 *  reopened and rebooked four times chasing the missing label). Merged
 *  into one document there is only one dialog to lose. If the merge
 *  fails (a PDF pdf-lib can't read) fall back to one dialog per label. */
export const printPdfsBase64 = async (list: string[]): Promise<boolean> => {
    if (list.length === 0) return true;
    if (list.length === 1) return printPdfBase64(list[0]);
    let merged: string | null = null;
    try {
        merged = await mergePdfsBase64(list);
    } catch {
        merged = null;
    }
    if (merged) return printPdfBase64(merged);
    for (const b64 of list) {
        if (!(await printPdfBase64(b64))) return false;
    }
    return true;
};

/** Send raw ZPL to the desk's Zebra: network agent first (when that is the
 *  active mode and a printer is saved), then the local agent. Returns the
 *  last failure so the caller can decide whether to fall back to PDF. */
export const printZplWithCascade = async (zpl: string, mode: PrintMode, copies = 1): Promise<PrintResult> => {
    let r: PrintResult = { success: false };
    const saved = getSavedNetworkPrinter();
    if (mode === "network" && saved) {
        r = await submitNetworkPrintJob(saved.agentId, saved.printerName, "", copies, null, false, { zpl });
    }
    if (!r.success) {
        r = await silentPrintZplRaw(zpl, copies, getSavedLocalPrinter() ?? undefined);
    }
    return r;
};

/** Hand the viewer a PDF file (base64 in, browser download out). */
export const downloadPdfBase64 = (b64: string, filename: string): void => {
    const url = URL.createObjectURL(new Blob([base64ToBytes(b64)], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
